import { ActiveTab } from "../components/Table/constants";
import type { ApplicationStageStatus } from "views/settings-crm/types/stage";

export type StageTabValue = `stage-${number}`;
export type TabValue = ActiveTab | StageTabValue;

const STAGE_TAB_PREFIX = "stage-";

export const stageTabValue = (id: number): StageTabValue =>
  `${STAGE_TAB_PREFIX}${id}`;

export const isStageTab = (tab: string): tab is StageTabValue =>
  tab.startsWith(STAGE_TAB_PREFIX);

export const stageIdFromTab = (tab: string): number | undefined => {
  if (!isStageTab(tab)) return undefined;
  const id = tab.slice(STAGE_TAB_PREFIX.length);
  return /^\d+$/.test(id) ? Number(id) : undefined;
};

const STATUS_TAB_KEYS: Record<
  Exclude<ActiveTab, ActiveTab.All>,
  ApplicationStageStatus
> = {
  [ActiveTab.NotSubmitted]: "not_submitted",
  [ActiveTab.PartiallySubmitted]: "partially_submitted",
  [ActiveTab.Submitted]: "submitted",
  [ActiveTab.OnHold]: "on_hold",
  [ActiveTab.Approved]: "approved",
  [ActiveTab.ApprovedConditionally]: "approved_conditionally",
  [ActiveTab.Declined]: "declined",
  [ActiveTab.Canceled]: "canceled"
};

const ALL_TAB_KEY = "all";

export const tabHiddenKey = (tab: TabValue): string => {
  if (isStageTab(tab)) return tab;
  if (tab === ActiveTab.All) return ALL_TAB_KEY;
  return STATUS_TAB_KEYS[tab];
};

export const filterVisibleTabs = <T extends { value: TabValue }>(
  tabs: T[],
  hiddenKeys: string[],
  selected: TabValue
): T[] => {
  const hidden = new Set(hiddenKeys);
  return tabs.filter(
    (tab) =>
      tab.value === ActiveTab.All ||
      tab.value === selected ||
      !hidden.has(tabHiddenKey(tab.value))
  );
};

export const toggleHiddenTab = (
  hiddenKeys: string[],
  tab: TabValue,
  visible: boolean
): string[] => {
  const key = tabHiddenKey(tab);
  const rest = hiddenKeys.filter((hiddenKey) => hiddenKey !== key);
  if (visible || tab === ActiveTab.All) return rest;
  return [...rest, key];
};

export const pruneHiddenTabs = (
  hiddenKeys: string[],
  stageIds: number[]
): string[] => {
  const existing = new Set(stageIds);
  return hiddenKeys.filter((key) => {
    if (!isStageTab(key)) return true;
    const id = stageIdFromTab(key);
    return id !== undefined && existing.has(id);
  });
};
